import React, {forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,} from "react";
import PropTypes from "prop-types";
import ReactDOM from "react-dom";
import toast from "react-hot-toast";
import "./NotesTab.css";

// Hooks
import {useNotesData} from "./hooks/useNotesData";
import {usePriorityManagement} from "./hooks/usePriorityManagement";
import {useNoteReplies} from "./hooks/useNoteReplies";
import {useInfiniteScroll} from "./hooks/useInfiniteScroll";
import {useNoteHover} from "./hooks/useNoteHover";
import {useUserStatus} from "./hooks/useUserStatus";
import {useLinkedNoteTooltip} from "./hooks/useLinkedNoteTooltip";
import {usePriorityHover} from "./hooks/usePriorityHover";
import {useStackedView} from "./hooks/useStackedView";
import {useLinkedNoteHandler} from "./hooks/useLinkedNoteHandler";

// Components
import {CardView, StackedView, TableView} from "./components/NoteViews";
import {NoteTextPopup} from "./components/Note";
import ReplyModal from "../Modals/ReplyModal";
import AiChatDialog from "../ai/AiChatDialog";

// Utils
import {INITIAL_PAGE_NUMBER, PAGE_SIZE} from "./utils/constants";
import {formatPriorityDate, formatTooltipContent} from "./utils/formatUtils";

const TooltipPortal = ({ children }) => ReactDOM.createPortal(children, document.body);

const NotesTab = memo(
  forwardRef(
    (
      {
        viewMode,
        finalDisplayNotes,
        isDataLoaded,
        initialLoading,
        searchLoading,
        loadingFiltered,
        loadingUniques,
        searchTerm,
        getActiveFilterCount,
        handleRowClick,
        handleRowDoubleClick,
        handleAddFromRow,
        handleEdit,
        handleDelete,
        handleViewAttachments,
        selectedRow,
        focusedRow,
        renderTableImageIcon,
        renderCardImageIcon,
        renderStackedImageIcon,
        expandedStacks,
        toggleStackExpansion,
        jobs,
        projects,
        tasks,
        handleFilterCheckboxChange,
        removeFilter,
        setViewNote,
        setShowViewModal,
        stackedJobs,
        loadingStackedJobs,
        fetchNotes,
        userId,
        hasActiveFilters,
        filteredNotesFromApi,
        searchResults,
        handleFilterChange,
        selectedFilters,
        fetchNotesForTask,
        loadingNotesByTask,
        notesByTask,
        fetchTasksForJob,
        tasksByJob,
        onUpdateTaskPriority,
        loadingTasksByJob,
        isClearingFilter,
        onFilterByProject,
        onFilterByWorkspace,
        refreshNotes,
        onFilterByTask,
        highlightedTaskId,
        clearHighlightedTask,
        loadMoreTaskNotes,
        taskPagination,
        workspaceRole,
        stackedState,
        setExpandedProjects,
        setExpandedTasks,
        setActiveTabByJob,
        setAssigneeFilters,
        setLastVisibleNoteInTask,
        workspacesMetadata,
        onRefreshWorkspacesMetadata,
        expandedWorkspaces,
        setExpandedWorkspaces,
      },
      ref,
    ) => {
      const {
        expandedProjects = {},
        expandedTasks = {},
        activeTabByJob = {},
        assigneeFilters = {},
      } = stackedState || {};
      const { lastVisibleNoteInTask } = stackedState || {};

      const [localNotes, setLocalNotes] = useState([]);
      const [page, setPage] = useState(INITIAL_PAGE_NUMBER);
      const [hasMore, setHasMore] = useState(true);
      const [loadingMore, setLoadingMore] = useState(false);
      const [isInitialLoad, setIsInitialLoad] = useState(true);
      const [isLoadingInitial, setIsLoadingInitial] = useState(false);
      const [showReplyModal, setShowReplyModal] = useState(false);
      const [selectedNoteForReply, setSelectedNoteForReply] = useState(null);
      const [showAiDialog, setShowAiDialog] = useState(false);
      const [aiJobContext, setAiJobContext] = useState(null);
      const [tasksByJobLocal, setTasksByJobLocal] = useState({});
      const [loadingTasksByJobLocal, setLoadingTasksByJobLocal] = useState({});

      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;
      const containerRef = useRef(null);
      const loadingRef = useRef(false);

      const isSearchMode = searchTerm && searchTerm.trim() !== "";

      // Search based stacked data
      const searchStackedData = useMemo(() => {
        if (!isSearchMode) return { jobs: [], tasksByJob: {}, notesByTask: {} };

        const workspacesMap = new Map();

        searchResults.forEach((note) => {
          const workspaceId = note.workspaceId;
          const workspaceName = note.workspace;
          const projectId = note.projectId;
          const projectName = note.project;
          const jobId = note.jobId;
          const jobName = note.job;
          const taskId = note.taskId;
          const taskName = note.task;
          const jobFriendlyId = note.jobFriendlyId || null;
          const taskFriendlyId = note.friendlyId || null; 

          if (!workspaceId || !jobId) return;

          if (!workspacesMap.has(workspaceId)) {
            workspacesMap.set(workspaceId, {
              workspaceId,
              workspaceName,
              projects: new Map(),
            });
          }
          const workspace = workspacesMap.get(workspaceId);

          if (projectId) {
            if (!workspace.projects.has(projectId)) {
              workspace.projects.set(projectId, {
                projectId,
                projectName,
                jobs: new Map(),
              });
            }
            const project = workspace.projects.get(projectId);
            if (!project.jobs.has(jobId)) {
              project.jobs.set(jobId, {
                jobId,
                jobName,
                tasks: new Map(),
                noteCount: 0,
                latestTimeStamp: null,
                lastSiteNote: null,
                lastSiteNoteUserName: null,
                siteNotes: [],
                friendlyId: jobFriendlyId,
              });
            }
            const job = project.jobs.get(jobId);
            if (!job.friendlyId && jobFriendlyId) job.friendlyId = jobFriendlyId;

            if (taskId) {
              if (!job.tasks.has(taskId)) {
                job.tasks.set(taskId, {
                  taskId,
                  title: taskName,
                  notes: [],
                  status: "Not Started",
                  userId: note.userId,
                  friendlyId: taskFriendlyId,
                });
              }
              const task = job.tasks.get(taskId);
              task.notes.push(note);
            } else {
              job.siteNotes.push(note);
            }

            job.noteCount++;
            if (!job.latestTimeStamp || new Date(note.timeStamp) > new Date(job.latestTimeStamp)) {
              job.latestTimeStamp = note.timeStamp;
              job.lastSiteNote = note.note;
              job.lastSiteNoteUserName = note.userName;
            }
          }
        });

        const stackedJobsArr = [];
        const tasksByJobMap = {};
        const notesByTaskMap = {};

        for (const ws of workspacesMap.values()) {
          for (const proj of ws.projects.values()) {
            for (const job of proj.jobs.values()) {
              const tasksArray = Array.from(job.tasks.values()).map((task) => ({
                id: task.taskId,
                title: task.title,
                status: task.status,
                userId: task.userId,
                jobId: job.jobId,
                notes: task.notes,
                noteCount: task.notes.length,
                friendlyId: task.friendlyId,
              }));
              tasksByJobMap[job.jobId] = tasksArray;
              tasksArray.forEach((task) => {
                notesByTaskMap[task.id] = task.notes;
              });

              stackedJobsArr.push({
                jobId: job.jobId,
                jobName: job.jobName,
                workspaceId: ws.workspaceId,
                workspaceName: ws.workspaceName,
                projectId: proj.projectId,
                projectName: proj.projectName,
                noteCount: job.noteCount,
                latestTimeStamp: job.latestTimeStamp,
                lastSiteNote: job.lastSiteNote,
                lastSiteNoteUserName: job.lastSiteNoteUserName,
                friendlyId: job.friendlyId || "N/A",
              });
            }
          }
        }

        return { jobs: stackedJobsArr, tasksByJob: tasksByJobMap, notesByTask: notesByTaskMap };
      }, [isSearchMode, searchResults]);

      const effectiveStackedJobs = isSearchMode ? searchStackedData.jobs : stackedJobs;
      const effectiveTasksByJob = isSearchMode ? searchStackedData.tasksByJob : tasksByJob;
      const effectiveNotesByTask = isSearchMode ? searchStackedData.notesByTask : notesByTask;

      const shouldFetchMore = !isSearchMode;
      const effectiveFetchTasksForJob = shouldFetchMore ? fetchTasksForJob : async () => {};
      const effectiveFetchNotesForTask = shouldFetchMore ? fetchNotesForTask : async () => {};
      const effectiveLoadMoreTaskNotes = shouldFetchMore ? loadMoreTaskNotes : async () => {};

      const {
        priorityTooltipData = {},
        loadingPriorityData = {},
        manuallyUpdatedPriorities = {},
        fetchPriorityData,
        handlePriorityClick,
        getPriorityValue,
      } = usePriorityManagement();

      const { displayNotes } = useNotesData({
        searchTerm,
        hasActiveFilters,
        searchResults,
        filteredNotesFromApi,
        localNotes,
        finalDisplayNotes,
        manuallyUpdatedPriorities,
      });

      const memoizedDisplayNotes = useMemo(() => displayNotes || [], [displayNotes]);

      const {
        isNoteReply,
        getReplyNoteId,
        isOriginalNoteExists,
      } = useNoteReplies({ userId, displayNotes: memoizedDisplayNotes });

      const {
        hoveredNoteContent,
        hoveredNoteId,
        notePopupPosition,
        noteElementRect,
        shouldShowNotePopup,
        handleNoteTextMouseEnter,
        handleNoteTextMouseLeave,
        handlePopupMouseEnter,
        handlePopupMouseLeave,
      } = useNoteHover({ viewMode });

      const { userStatusMap, loadingUsers, fetchUserStatus, getUserStatusStyle } = useUserStatus();

      const {
        hoveredLinkedNote,
        tooltipPosition,
        linkedNoteContent,
        loadingLinkedNote,
        handleLinkedNoteMouseEnter,
        handleLinkedNoteMouseLeave,
        handleLinkedNoteClick: handleLinkedNoteClickHook,
      } = useLinkedNoteTooltip({ getReplyNoteId });

      const safeFetchPriorityData = useCallback(
        (noteId) => fetchPriorityData?.(noteId) || Promise.resolve(),
        [fetchPriorityData],
      );

      const {
        hoveredPriorityNote,
        priorityTooltipPosition,
        handlePriorityMouseEnter,
        handlePriorityMouseLeave,
      } = usePriorityHover({
        fetchPriorityData: safeFetchPriorityData,
        priorityTooltipData: priorityTooltipData || {},
      });

      const { isStackedViewLoading, isFilteringStacked } = useStackedView({
        viewMode,
        hasActiveFilters,
      });

      // ========== Memoized derived data ==========
      const jobsToDisplay = useMemo(
        () => (viewMode === "stacked" ? effectiveStackedJobs || [] : []),
        [viewMode, effectiveStackedJobs],
      );

      const effectiveTasksByJobMemo = useMemo(
        () => effectiveTasksByJob || tasksByJobLocal,
        [effectiveTasksByJob, tasksByJobLocal],
      );

      const effectiveLoadingTasksByJob = useMemo(
        () => loadingTasksByJob || loadingTasksByJobLocal,
        [loadingTasksByJob, loadingTasksByJobLocal],
      );

      // ========== Callbacks ==========
      const handleProjectClick = useCallback(
        (projectName) => {
          const project = (projects || []).find(
            (p) =>
              p.name === projectName ||
              p.projectName === projectName ||
              p.text === projectName,
          );
          if (!project) {
            console.error("Project not found:", projectName);
            return;
          }
          const projectId = project.id || project.projectId;
          const projectDisplayText = project.displayName || project.displayText || projectName;
          handleFilterCheckboxChange?.("project", projectId, {
            id: projectId,
            text: projectName,
            displayText: projectDisplayText,
          });
        },
        [projects, handleFilterCheckboxChange],
      );

      const handleJobClick = useCallback(
        (jobName) => {
          const job = (jobs || []).find(
            (j) => j.name === jobName || j.jobName === jobName || j.text === jobName,
          );
          if (!job) {
            console.error("Job not found:", jobName);
            return;
          }
          const jobId = job.id || job.jobId;
          const jobDisplayText = job.displayName || job.displayText || jobName;
          handleFilterCheckboxChange?.("job", jobId, {
            id: jobId,
            text: jobName,
            displayText: jobDisplayText,
          });
        },
        [jobs, handleFilterCheckboxChange],
      );

      const handleTaskClick = useCallback(
        (taskName) => {
          const task = (tasks || []).find(
            (t) => t.title === taskName || t.taskName === taskName || t.text === taskName,
          );
          if (!task) {
            console.error("task not found:", taskName);
            return;
          }
          const taskId = task.id;
          const taskDisplayText = task.title;
          handleFilterCheckboxChange?.("task", taskId, {
            id: taskId,
            text: taskName,
            displayText: taskDisplayText,
          });
        },
        [tasks, handleFilterCheckboxChange],
      );

      const handleUserNameClick = useCallback(
        (userName) => {
          handleFilterCheckboxChange?.("userName", userName, {
            id: userName,
            text: userName,
            displayText: userName,
          });
        },
        [handleFilterCheckboxChange],
      );

      const handleReplyToNote = useCallback((note) => {
        setSelectedNoteForReply(note);
        setShowReplyModal(true);
      }, []);

      const refreshNotesAfterReply = useCallback(async () => {
        if (fetchNotes) {
          const result = await fetchNotes(1, PAGE_SIZE);
          if (result && result.notes) {
            setLocalNotes([...result.notes].sort((a, b) => b.id - a.id));
            setHasMore(result.hasMore);
            setPage(2);
            setIsInitialLoad(false);
          }
        }
      }, [fetchNotes]);

      const handleOpenAiDialogForJob = useCallback((job) => {
        setAiJobContext({ jobId: job.jobId, jobName: job.jobName });
        setShowAiDialog(true);
      }, []);

      const closeAiDialog = useCallback(() => {
        setShowAiDialog(false);
        setAiJobContext(null);
      }, []);

      const { handleLinkedNoteClick } = useLinkedNoteHandler({
        handleLinkedNoteClickHook,
        memoizedDisplayNotes,
        handleRowClick,
        viewMode,
      });

      const localFetchTasksForJob = useCallback(
        async (jobId) => {
          // PREVENT FETCHING IN SEARCH MODE – data already present
          if (isSearchMode) return;
          if (effectiveFetchTasksForJob) return effectiveFetchTasksForJob(jobId);
          if (!jobId || loadingTasksByJobLocal[jobId]) return;
          setLoadingTasksByJobLocal((prev) => ({ ...prev, [jobId]: true }));
          try {
            const response = await fetch(`${apiUrl}/JobTasks/job/${jobId}`);
            const result = await response.json();
            if (result.success && result.data) {
              setTasksByJobLocal((prev) => ({ ...prev, [jobId]: result.data }));
              return result.data;
            }
          } catch (error) {
            console.error("Error fetching tasks:", error);
            toast.error("Failed to load tasks");
          } finally {
            setLoadingTasksByJobLocal((prev) => ({ ...prev, [jobId]: false }));
          }
        },
        [isSearchMode, effectiveFetchTasksForJob, apiUrl, loadingTasksByJobLocal],
      );

      // ========== toggleTaskExpansion using lifted state ==========
      const toggleTaskExpansion = useCallback(
        (taskId, jobId) => {
          setExpandedTasks((prev) => {
            const isExpanding = !prev[taskId];
            if (isExpanding && (!effectiveNotesByTask[taskId] || effectiveNotesByTask[taskId].length === 0)) {
              effectiveFetchNotesForTask?.(taskId, jobId);
            }
            return { ...prev, [taskId]: isExpanding };
          });
        },
        [effectiveNotesByTask, effectiveFetchNotesForTask, setExpandedTasks],
      );

      // ========== Handlers for tabs and assignee filters ==========
      const handleTabChange = useCallback(
        (jobId, statusId) => {
          setActiveTabByJob((prev) => ({ ...prev, [jobId]: statusId }));
        },
        [setActiveTabByJob],
      );

      const handleAssigneeFilter = useCallback(
        (jobId, userId) => {
          setAssigneeFilters((prev) => {
            const current = prev[jobId] || [];
            const newFilters = current.includes(userId)
              ? current.filter((id) => id !== userId)
              : [...current, userId];
            return { ...prev, [jobId]: newFilters.length ? newFilters : null };
          });
        },
        [setAssigneeFilters],
      );

      const clearAssigneeFilter = useCallback(
        (jobId) => {
          setAssigneeFilters((prev) => ({ ...prev, [jobId]: null }));
        },
        [setAssigneeFilters],
      );

      const handleStackToggle = useCallback(
        (jobName, jobId) => {
          toggleStackExpansion(jobName, jobId);
          const isExpanding = !expandedStacks[jobName];
          if (isExpanding) localFetchTasksForJob(jobId);
        },
        [toggleStackExpansion, expandedStacks, localFetchTasksForJob],
      );

      // Filter check helpers (memoized)
      const isProjectFiltered = useCallback(
        (projectName) => {
          if (!selectedFilters?.project?.length) return false;
          const project = (projects || []).find(
            (p) => p.name === projectName || p.projectName === projectName || p.text === projectName,
          );
          if (!project) return false;
          const projectId = project.id || project.projectId;
          return selectedFilters.project.includes(projectId);
        },
        [selectedFilters, projects],
      );

      const isJobFiltered = useCallback(
        (jobName) => {
          if (!selectedFilters?.job?.length) return false;
          const job = (jobs || []).find(
            (j) => j.name === jobName || j.jobName === jobName || j.text === jobName,
          );
          if (!job) return false;
          const jobId = job.id || job.jobId;
          return selectedFilters.job.includes(jobId);
        },
        [selectedFilters, jobs],
      );

      const isTaskFiltered = useCallback(
        (taskName) => selectedFilters?.task?.includes(taskName) || false,
        [selectedFilters],
      );

      const isUserNameFiltered = useCallback(
        (userName) => selectedFilters?.userName?.includes(userName) || false,
        [selectedFilters],
      );

      // ========== Infinite scroll ==========
      const loadMoreNotes = useCallback(async () => {
        if (loadingRef.current || !hasMore || hasActiveFilters || searchTerm.trim()) return;
        loadingRef.current = true;
        setLoadingMore(true);
        try {
          const result = await fetchNotes(page, PAGE_SIZE);
          if (result?.notes?.length) {
            setLocalNotes((prev) => {
              const existingIds = new Set(prev.map((n) => n.id));
              const newNotes = result.notes.filter((n) => !existingIds.has(n.id));
              return [...prev, ...newNotes.sort((a, b) => b.id - a.id)];
            });
            setHasMore(result.hasMore);
            setPage((prev) => prev + 1);
          } else {
            setHasMore(false);
          }
        } catch (error) {
          console.error("Error loading more notes:", error);
        } finally {
          loadingRef.current = false;
          setLoadingMore(false);
        }
      }, [fetchNotes, page, hasMore, hasActiveFilters, searchTerm]);

      const { lastRowRef, lastCardRef } = useInfiniteScroll({
        viewMode,
        hasMore,
        loadingMore,
        hasActiveFilters,
        searchTerm,
        loadMoreNotes,
        displayNotes: memoizedDisplayNotes,
      });

      // ========== Initial load ==========
      const loadInitialNotes = useCallback(async () => {
        if (loadingRef.current || (finalDisplayNotes?.length > 0)) return;
        loadingRef.current = true;
        setIsLoadingInitial(true);
        try {
          const result = await fetchNotes(INITIAL_PAGE_NUMBER, PAGE_SIZE);
          if (result?.notes) {
            setLocalNotes([...result.notes].sort((a, b) => b.id - a.id));
            setHasMore(result.hasMore);
            setPage(INITIAL_PAGE_NUMBER + 1);
          }
        } catch (error) {
          console.error("Error loading initial notes:", error);
        } finally {
          loadingRef.current = false;
          setIsLoadingInitial(false);
          setIsInitialLoad(false);
        }
      }, [fetchNotes, finalDisplayNotes]);

      useEffect(() => {
        if (isDataLoaded && !hasActiveFilters && !searchTerm.trim() && isInitialLoad) {
          if (!finalDisplayNotes?.length) loadInitialNotes();
          else setIsInitialLoad(false);
        }
      }, [isDataLoaded, hasActiveFilters, searchTerm, isInitialLoad, loadInitialNotes, finalDisplayNotes]);

      useEffect(() => {
        if (finalDisplayNotes?.length && !hasActiveFilters && !searchTerm.trim() && isInitialLoad) {
          const sortedNotes = [...finalDisplayNotes].sort((a, b) => b.id - a.id);
          setLocalNotes(sortedNotes);
          setHasMore(finalDisplayNotes.length >= PAGE_SIZE);
          setIsInitialLoad(false);
        }
      }, [finalDisplayNotes, hasActiveFilters, searchTerm, isInitialLoad]);

      useEffect(() => {
        if (!hasActiveFilters && !searchTerm.trim() && !isInitialLoad) {
          setHasMore(true);
          setPage(1);
          if (finalDisplayNotes?.length) {
            setLocalNotes([...finalDisplayNotes].sort((a, b) => b.id - a.id));
            setPage(2);
            setHasMore(finalDisplayNotes.length >= PAGE_SIZE);
          }
        }
      }, [hasActiveFilters, searchTerm, isInitialLoad, finalDisplayNotes]);

      // ========== Highlight note (exposed to parent) ==========
      const highlightNote = useCallback((noteId) => {
        setTimeout(() => {
          const noteElement = document.querySelector(`[data-note-id="${noteId}"]`);
          if (noteElement) {
            noteElement.classList.remove("highlight-original-note");
            void noteElement.offsetHeight;
            noteElement.scrollIntoView({ behavior: "smooth", block: "center" });
            setTimeout(() => {
              noteElement.classList.add("highlight-original-note");
              setTimeout(() => noteElement.classList.remove("highlight-original-note"), 3000);
            }, 100);
          } else {
            console.warn(`Note element with ID ${noteId} not found`);
          }
        }, 500);
      }, []);

      useImperativeHandle(ref, () => ({
        highlightNote,
        scrollToAndHighlightNote: highlightNote,
        getNoteElement: (noteId) => document.querySelector(`[data-note-id="${noteId}"]`),
      }));

      // ========== Render helpers ==========
      const isLoading = !isDataLoaded || initialLoading || searchLoading || loadingFiltered || loadingUniques || isLoadingInitial;

      // Memoize common props for views
      const commonProps = useMemo(
        () => ({
          displayNotes: memoizedDisplayNotes,
          selectedRow,
          searchTerm,
          handleRowClick,
          handleRowDoubleClick: (note) => {
            handleRowDoubleClick(note);
            if (viewMode === "table") {
              const job = jobs.find((j) => String(j.id) === String(note.job) || j.name === note.job);
              setViewNote({ id: note.id, jobId: job?.id ?? null });
              setShowViewModal(true);
            }
          },
          handleAddFromRow,
          handleEdit,
          handleDelete,
          handleViewAttachments,
          handleReplyToNote,
          handlePriorityClick,
          handlePriorityMouseEnter,
          handlePriorityMouseLeave,
          handleNoteTextMouseEnter,
          handleNoteTextMouseLeave,
          handleLinkedNoteClick,
          handleLinkedNoteMouseEnter,
          handleLinkedNoteMouseLeave,
          isNoteReply,
          getReplyNoteId,
          isOriginalNoteExists,
          userStatusMap,
          loadingUsers,
          getPriorityValue,
          manuallyUpdatedPriorities,
          shouldShowNotePopup,
          handleProjectClick,
          handleJobClick,
          handleTaskClick,
          handleUserNameClick,
          isProjectFiltered,
          isJobFiltered,
          isTaskFiltered,
          isUserNameFiltered,
        }),
        [
          memoizedDisplayNotes,
          selectedRow,
          searchTerm,
          handleRowClick,
          handleRowDoubleClick,
          jobs,
          setViewNote,
          setShowViewModal,
          viewMode,
          handleAddFromRow,
          handleEdit,
          handleDelete,
          handleViewAttachments,
          handleReplyToNote,
          handlePriorityClick,
          handlePriorityMouseEnter,
          handlePriorityMouseLeave,
          handleNoteTextMouseEnter,
          handleNoteTextMouseLeave,
          handleLinkedNoteClick,
          handleLinkedNoteMouseEnter,
          handleLinkedNoteMouseLeave,
          isNoteReply,
          getReplyNoteId,
          isOriginalNoteExists,
          userStatusMap,
          loadingUsers,
          getPriorityValue,
          manuallyUpdatedPriorities,
          shouldShowNotePopup,
          handleProjectClick,
          handleJobClick,
          handleTaskClick,
          handleUserNameClick,
          isProjectFiltered,
          isJobFiltered,
          isTaskFiltered,
          isUserNameFiltered,
        ],
      );

      const renderView = useMemo(() => {
        switch (viewMode) {
          case "table":
            return (
              <TableView
                {...commonProps}
                isLoading={isLoading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                hasActiveFilters={hasActiveFilters}
                lastRowRef={lastRowRef}
                jobs={jobs}
                setViewNote={setViewNote}
                setShowViewModal={setShowViewModal}
                renderTableImageIcon={renderTableImageIcon}
                focusedRow={focusedRow}
                userId={userId}
              />
            );
          case "stacked":
            return (
              <StackedView
                {...commonProps}
                selectedFilters={selectedFilters}
                stackedJobs={effectiveStackedJobs}
                loadingStackedJobs={loadingStackedJobs && !isSearchMode}
                expandedStacks={expandedStacks}
                toggleStackExpansion={handleStackToggle}
                hasActiveFilters={hasActiveFilters}
                searchTerm={searchTerm}
                openAiDialogForJob={handleOpenAiDialogForJob}
                renderStackedImageIcon={renderStackedImageIcon}
                isStackedViewLoading={isStackedViewLoading}
                isFilteringStacked={isFilteringStacked}
                jobsToDisplay={jobsToDisplay}
                tasksByJob={effectiveTasksByJobMemo}
                loadingTasksByJob={effectiveLoadingTasksByJob}
                notesByTask={effectiveNotesByTask}
                loadingNotesByTask={loadingNotesByTask}
                fetchTasksForJob={localFetchTasksForJob}
                fetchNotesForTask={effectiveFetchNotesForTask}
                userId={userId}
                isClearingFilter={isClearingFilter}
                onFilterByProject={onFilterByProject}
                onFilterByWorkspace={onFilterByWorkspace}
                refreshNotes={refreshNotes}
                highlightedTaskId={highlightedTaskId}
                clearHighlightedTask={clearHighlightedTask}
                loadMoreTaskNotes={effectiveLoadMoreTaskNotes}
                taskPagination={taskPagination}
                workspaceRole={workspaceRole}
                // Lifted state props
                expandedProjects={expandedProjects}
                setExpandedProjects={setExpandedProjects}
                expandedTasks={expandedTasks}
                toggleTaskExpansion={toggleTaskExpansion}
                activeTabByJob={activeTabByJob}
                handleTabChange={handleTabChange}
                assigneeFilters={assigneeFilters}
                handleAssigneeFilter={handleAssigneeFilter}
                clearAssigneeFilter={clearAssigneeFilter}
                lastVisibleNoteInTask={lastVisibleNoteInTask}
                setLastVisibleNoteInTask={setLastVisibleNoteInTask}
                setExpandedTasks={setExpandedTasks}
                workspacesMetadata={workspacesMetadata}
                onRefreshWorkspacesMetadata={onRefreshWorkspacesMetadata}
                expandedWorkspaces={expandedWorkspaces}
                setExpandedWorkspaces={setExpandedWorkspaces}
                onPriorityUpdate={onUpdateTaskPriority} 
              />
            );
          default: // cards
            return (
              <CardView
                {...commonProps}
                isLoading={isLoading}
                loadingMore={loadingMore}
                hasMore={hasMore}
                hasActiveFilters={hasActiveFilters}
                lastCardRef={lastCardRef}
                renderCardImageIcon={renderCardImageIcon}
                viewMode={viewMode}
                userId={userId}
                onFilterByProject={onFilterByProject}
              />
            );
        }
      }, [
        viewMode,
        commonProps,
        isLoading,
        loadingMore,
        hasMore,
        hasActiveFilters,
        lastRowRef,
        lastCardRef,
        jobs,
        setViewNote,
        setShowViewModal,
        renderTableImageIcon,
        renderCardImageIcon,
        renderStackedImageIcon,
        focusedRow,
        userId,
        effectiveStackedJobs,
        loadingStackedJobs,
        isSearchMode,
        expandedStacks,
        handleStackToggle,
        searchTerm,
        handleOpenAiDialogForJob,
        isStackedViewLoading,
        isFilteringStacked,
        jobsToDisplay,
        effectiveTasksByJobMemo,
        effectiveLoadingTasksByJob,
        effectiveNotesByTask,
        loadingNotesByTask,
        localFetchTasksForJob,
        effectiveFetchNotesForTask,
        isClearingFilter,
        onFilterByProject,
        onFilterByWorkspace,
        refreshNotes,
        highlightedTaskId,
        clearHighlightedTask,
        effectiveLoadMoreTaskNotes,
        taskPagination,
        workspaceRole,
        expandedProjects,
        setExpandedProjects,
        expandedTasks,
        toggleTaskExpansion,
        activeTabByJob,
        handleTabChange,
        assigneeFilters,
        handleAssigneeFilter,
        clearAssigneeFilter,
        lastVisibleNoteInTask,
        setLastVisibleNoteInTask,
        setExpandedTasks,
        workspacesMetadata,
        onRefreshWorkspacesMetadata,
        expandedWorkspaces,
        setExpandedWorkspaces,
      ]);

      // ========== Hovered note for tooltip (must be before conditional return) ==========
      const hoveredNote = useMemo(
        () => memoizedDisplayNotes.find((n) => n.id === hoveredLinkedNote),
        [memoizedDisplayNotes, hoveredLinkedNote],
      );
      const hoveredOriginalNoteId = hoveredNote ? getReplyNoteId(hoveredNote.id) : null;

      // ========== LOADING SPINNER FOR SEARCH MODE (after all hooks) ==========
      if (viewMode === "stacked" && isSearchMode && searchLoading) {
  return (
    <div className="stacked-view-loading-container">
      
        <i className="fas fa-spinner fa-spin spinner-icon" />
       
      
    </div>
  );
}

      return (
        <div className="grid-scroll-container" ref={containerRef}>
          {renderView}

          {/* Note Text Popup */}
          {hoveredNoteContent && (
            <div
              className="note-text-popup-wrapper"
              onMouseEnter={handlePopupMouseEnter}
              onMouseLeave={handlePopupMouseLeave}
            >
              <NoteTextPopup
                content={hoveredNoteContent}
                position={notePopupPosition}
                elementRect={noteElementRect}
                searchTerm={searchTerm}
                viewMode={viewMode}
                onClose={() => {}}
              />
            </div>
          )}

          {/* Linked Note Tooltip Portal */}
          {hoveredLinkedNote && hoveredOriginalNoteId && (
            <TooltipPortal>
              <div
                className="linked-note-tooltip"
                style={{
                  position: "fixed",
                  top: `${tooltipPosition.y}px`,
                  left: `${tooltipPosition.x}px`,
                  zIndex: 999999,
                }}
              >
                <div className="linked-note-tooltip-header">
                  <i className="fas fa-link" />
                  Linked to Original Note #{hoveredOriginalNoteId}
                </div>

                {loadingLinkedNote[hoveredOriginalNoteId] ? (
                  <div className="tooltip-loading">
                    <i className="fas fa-spinner fa-spin" /> Loading original note...
                  </div>
                ) : linkedNoteContent[hoveredOriginalNoteId] ? (
                  <>
                    <div className="linked-note-content-box">
                      <strong>Content:</strong>{" "}
                      {formatTooltipContent(linkedNoteContent[hoveredOriginalNoteId].content)}
                    </div>
                    <div className="linked-note-metadata">
                      <div>
                        <i className="fas fa-user" />
                        <strong>By:</strong> {linkedNoteContent[hoveredOriginalNoteId].userName}
                      </div>
                      <div>
                        <i className="far fa-clock" />
                        <strong>Date:</strong>{" "}
                        {new Date(linkedNoteContent[hoveredOriginalNoteId].date).toLocaleDateString()}{" "}
                        {new Date(linkedNoteContent[hoveredOriginalNoteId].date).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </div>
                      {(linkedNoteContent[hoveredOriginalNoteId].workspace ||
                        linkedNoteContent[hoveredOriginalNoteId].project ||
                        linkedNoteContent[hoveredOriginalNoteId].job) && (
                        <div>
                          <i className="fas fa-map-marker-alt" />
                          <strong>Location:</strong> {linkedNoteContent[hoveredOriginalNoteId].workspace} /{" "}
                          {linkedNoteContent[hoveredOriginalNoteId].project} /{" "}
                          {linkedNoteContent[hoveredOriginalNoteId].job}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="tooltip-no-results">
                    <i className="fas fa-info-circle" /> Hover to load original note preview
                  </div>
                )}

                <div className="linked-note-click-hint">
                  <i className="fas fa-mouse-pointer" /> Click to jump to original note
                </div>

                <div className="linked-note-tooltip-arrow" />
              </div>
            </TooltipPortal>
          )}

          {/* Priority Tooltip Portal */}
          {hoveredPriorityNote && (
            <TooltipPortal>
              <div
                className="priority-tooltip"
                style={{
                  top: `${priorityTooltipPosition.y}px`,
                  left: `${priorityTooltipPosition.x}px`,
                }}
              >
                <div className="priority-tooltip-header">
                  <i
                    className="fas fa-flag"
                    style={{
                      color:
                        priorityTooltipData[hoveredPriorityNote]?.priorityValue === 4
                          ? "#e8f628"
                          : priorityTooltipData[hoveredPriorityNote]?.priorityValue === 3
                            ? "#ef5350"
                            : priorityTooltipData[hoveredPriorityNote]?.priorityValue === 5
                              ? "#28a745"
                              : "#ccc",
                    }}
                  />
                  {loadingPriorityData[hoveredPriorityNote]
                    ? "Loading priority info..."
                    : `Priority: ${priorityTooltipData[hoveredPriorityNote]?.priorityText || "No priority"}`}
                </div>

                {loadingPriorityData[hoveredPriorityNote] ? (
                  <div className="tooltip-loading">
                    <i className="fas fa-spinner fa-spin" /> Loading priority information...
                  </div>
                ) : priorityTooltipData[hoveredPriorityNote]?.hasPriority ? (
                  <div className="priority-info-content">
                    <div>
                      <i className="fas fa-user" />
                      <strong>Set by:</strong> {priorityTooltipData[hoveredPriorityNote]?.userName || "Unknown"}
                    </div>
                    <div>
                      <i className="fas fa-calendar-alt" />
                      <strong>Date:</strong>{" "}
                      {formatPriorityDate(priorityTooltipData[hoveredPriorityNote]?.createdAt)}
                    </div>
                  </div>
                ) : (
                  <div className="no-priority-message">
                    <i className="fas fa-info-circle" />
                    No priority set for this note
                    <div className="small-hint">Click to set priority</div>
                  </div>
                )}
              </div>
            </TooltipPortal>
          )}

          {/* Modals */}
          {showAiDialog && aiJobContext && (
            <AiChatDialog open={showAiDialog} onClose={closeAiDialog} job={aiJobContext} userId={userId} />
          )}
          {showReplyModal && selectedNoteForReply && (
            <ReplyModal
              note={selectedNoteForReply}
              onClose={() => {
                setShowReplyModal(false);
                setSelectedNoteForReply(null);
              }}
              refreshNotes={refreshNotesAfterReply}
            />
          )}
        </div>
      );
    },
  ),
);

NotesTab.propTypes = {
  viewMode: PropTypes.string.isRequired,
  finalDisplayNotes: PropTypes.array,
  isDataLoaded: PropTypes.bool.isRequired,
  initialLoading: PropTypes.bool.isRequired,
  searchLoading: PropTypes.bool.isRequired,
  loadingFiltered: PropTypes.bool.isRequired,
  loadingUniques: PropTypes.bool.isRequired,
  searchTerm: PropTypes.string.isRequired,
  getActiveFilterCount: PropTypes.func.isRequired,
  handleRowClick: PropTypes.func.isRequired,
  handleRowDoubleClick: PropTypes.func.isRequired,
  handleAddFromRow: PropTypes.func.isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleViewAttachments: PropTypes.func.isRequired,
  selectedRow: PropTypes.any,
  focusedRow: PropTypes.any,
  renderTableImageIcon: PropTypes.func.isRequired,
  renderCardImageIcon: PropTypes.func.isRequired,
  renderStackedImageIcon: PropTypes.func.isRequired,
  expandedStacks: PropTypes.object.isRequired,
  toggleStackExpansion: PropTypes.func.isRequired,
  jobs: PropTypes.array.isRequired,
  setViewNote: PropTypes.func.isRequired,
  setShowViewModal: PropTypes.func.isRequired,
  stackedJobs: PropTypes.array,
  loadingStackedJobs: PropTypes.bool,
  fetchNotes: PropTypes.func.isRequired,
  userId: PropTypes.number.isRequired,
  hasActiveFilters: PropTypes.bool.isRequired,
  filteredNotesFromApi: PropTypes.array.isRequired,
  searchResults: PropTypes.array.isRequired,
  handleFilterCheckboxChange: PropTypes.func,
  removeFilter: PropTypes.func,
  projects: PropTypes.array,
  selectedFilters: PropTypes.object,
  handleFilterChange: PropTypes.func,
  fetchNotesForTask: PropTypes.func,
  loadingNotesByTask: PropTypes.object,
  notesByTask: PropTypes.object,
  fetchTasksForJob: PropTypes.func,
  tasksByJob: PropTypes.object,
  loadingTasksByJob: PropTypes.object,
  isClearingFilter: PropTypes.bool,
  onFilterByProject: PropTypes.func,
  onFilterByWorkspace: PropTypes.func,
  refreshNotes: PropTypes.func,
  onFilterByTask: PropTypes.func,
  highlightedTaskId: PropTypes.number,
  clearHighlightedTask: PropTypes.func,
  loadMoreTaskNotes: PropTypes.func,
  taskPagination: PropTypes.object,
  stackedState: PropTypes.object,
  setExpandedProjects: PropTypes.func,
  setExpandedTasks: PropTypes.func,
  setActiveTabByJob: PropTypes.func,
  setAssigneeFilters: PropTypes.func,
  setLastVisibleNoteInTask: PropTypes.func,
  workspacesMetadata: PropTypes.array,
  onRefreshWorkspacesMetadata: PropTypes.func,
  expandedWorkspaces: PropTypes.object,
  setExpandedWorkspaces: PropTypes.func,
};

NotesTab.defaultProps = {
  finalDisplayNotes: [],
  stackedJobs: [],
  loadingStackedJobs: false,
  filteredNotesFromApi: [],
  searchResults: [],
  handleFilterCheckboxChange: () => console.warn("handleFilterCheckboxChange not implemented"),
  removeFilter: () => console.warn("removeFilter not implemented"),
  selectedFilters: {},
  projects: [],
  handleFilterChange: () => console.warn("handleFilterChange not implemented"),
  fetchNotesForTask: () => {},
  loadingNotesByTask: {},
  notesByTask: {},
  fetchTasksForJob: () => {},
  tasksByJob: {},
  loadingTasksByJob: {},
  isClearingFilter: false,
  onFilterByProject: () => {},
  onFilterByWorkspace: () => {},
  refreshNotes: () => {},
  onFilterByTask: () => {},
  highlightedTaskId: null,
  clearHighlightedTask: () => {},
  loadMoreTaskNotes: () => {},
  taskPagination: {},
  stackedState: { expandedProjects: {}, expandedTasks: {}, activeTabByJob: {}, assigneeFilters: {} },
  setExpandedProjects: () => {},
  setExpandedTasks: () => {},
  setActiveTabByJob: () => {},
  setAssigneeFilters: () => {},
  setLastVisibleNoteInTask: () => {},
  workspacesMetadata: [],
  onRefreshWorkspacesMetadata: () => {},
  expandedWorkspaces: {},
  setExpandedWorkspaces: () => {},
};

export default NotesTab;